// ============================================================================
// PaywallEmailCTA — bouton d'opt-in explicite sur les paywalls natif (iOS + Android).
// ============================================================================
// Utilisé PARTOUT où un paywall serait montré à un élève, mais UNIQUEMENT quand
// `isNativeApp()` est vrai (App Store Guideline 3.1.1 + Google Play Reader Apps
// policy : pas de CTA d'achat ni de lien Stripe dans une app native qui donne
// accès à du contenu payant sans passer par IAP / Google Play Billing).
//
// Le composant enclenche l'edge fn `send-paywall-email` qui, opt-in explicite
// du user, envoie un email avec les formules et un lien vers le site marchand
// (Guideline 3.1.3, update 2024, autorise EXPLICITEMENT ce pattern).
//
// Le libellé du bouton est VOLONTAIREMENT neutre (« Recevoir les infos par
// email ») : Apple interdit les mots « Souscrire / Acheter / Débloquer »
// couplés à un flow qui mène vers du paiement externe.
//
// Le mail est routé automatiquement :
//   1. Élève < 15 ans → parent RGPD (parental_consent_email)
//   2. Élève avec parent lié (parent_links actif) → email du parent
//   3. Sinon → élève lui-même
// Le composant affiche un hint discret (email masqué) pour que l'élève sache
// quelle boîte consulter, sans exposer l'adresse complète du parent.
// ============================================================================

import { useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';

type Status = 'idle' | 'loading' | 'sent' | 'rate_limited' | 'error';

interface SendPaywallEmailResponse {
  ok?: boolean;
  email_sent?: boolean;
  recipient_kind?: 'student' | 'parent_link' | 'parental_consent';
  recipient_hint?: string;
  reason?: string;
  /** Champs de l'erreur 429 renvoyée par l'edge fn */
  error?: string;
  next_at?: string;
  last_sent_at?: string;
}

interface Props {
  /**
   * Contexte libre (chapter_type, chapter_slug, level, exercise_id, attempt_id,
   * objective_slug…) — persisté dans `paywall_email_logs.context` pour analytics.
   * Aucun impact fonctionnel.
   */
  context?: Record<string, unknown>;
  className?: string;
}

const RECIPIENT_LABEL: Record<NonNullable<SendPaywallEmailResponse['recipient_kind']>, string> = {
  student: 'ta boîte',
  parent_link: 'la boîte de ton parent',
  parental_consent: 'la boîte de ton parent',
};

/**
 * Formate une durée relative en heures pleines : "3 heures", "1 heure".
 * Compact et lisible côté élève sans dépendre d'une lib i18n.
 */
function formatHoursFromNow(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const hours = Math.max(1, Math.ceil(ms / (3600 * 1000)));
  return hours === 1 ? '1 heure' : `${hours} heures`;
}

export function PaywallEmailCTA({ context, className }: Props) {
  const [status, setStatus] = useState<Status>('idle');
  const [response, setResponse] = useState<SendPaywallEmailResponse | null>(null);

  async function handleSend() {
    setStatus('loading');
    const { data, error } = await supabase.functions.invoke<SendPaywallEmailResponse>(
      'send-paywall-email',
      {
        body: { context: context ?? {} },
      },
    );

    if (error) {
      // Supabase functions.invoke expose le status HTTP + parfois le body dans
      // error.context (selon la version). On lit défensivement.
      const errCtx = (error as unknown as { context?: SendPaywallEmailResponse }).context;
      const httpStatus = (error as unknown as { status?: number }).status;
      if (httpStatus === 429 || errCtx?.error === 'rate_limited') {
        setResponse(errCtx ?? null);
        setStatus('rate_limited');
        return;
      }
      setStatus('error');
      return;
    }

    if (data?.email_sent) {
      setResponse(data);
      setStatus('sent');
      return;
    }

    // email_sent = false mais pas d'erreur : SMTP non config OU déjà abonné.
    // Dans les 2 cas, message générique — pas de leak d'info technique.
    setStatus('error');
  }

  if (status === 'sent') {
    const boxLabel = response?.recipient_kind
      ? RECIPIENT_LABEL[response.recipient_kind]
      : 'ta boîte';
    return (
      <div className={cn('border-xp-200 bg-xp-50 rounded-xl border p-4 text-center', className)}>
        <p className="text-xp-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
          Envoyé
        </p>
        <p className="font-display text-ink-900 mt-1 text-base font-extrabold">📧 Email envoyé !</p>
        <p className="text-ink-600 mt-1.5 font-sans text-xs leading-relaxed">
          Ouvre {boxLabel}
          {response?.recipient_hint ? (
            <>
              {' '}
              (<span className="font-mono">{response.recipient_hint}</span>)
            </>
          ) : null}{' '}
          pour découvrir les formules disponibles.
        </p>
      </div>
    );
  }

  if (status === 'rate_limited') {
    const inHours = formatHoursFromNow(response?.next_at);
    return (
      <div className={cn('border-ink-200 bg-ink-50 rounded-xl border p-4 text-center', className)}>
        <p className="font-display text-ink-900 text-sm font-bold">Email déjà envoyé récemment</p>
        <p className="text-ink-500 mt-1 font-sans text-xs leading-relaxed">
          {inHours
            ? `Tu pourras en redemander un dans ${inHours}. Vérifie tes spams si tu ne le vois pas.`
            : 'Un nouvel envoi sera possible dans quelques heures. Vérifie tes spams si tu ne le vois pas.'}
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        onClick={handleSend}
        disabled={status === 'loading'}
        className="bg-primary-400 font-display hover:bg-primary-500 min-h-11 w-full rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98] disabled:opacity-60"
      >
        {status === 'loading' ? 'Envoi…' : '📧 Recevoir les infos par email'}
      </button>
      {status === 'error' && (
        <p className="text-danger-500 mt-2 text-center font-sans text-xs">
          Une erreur est survenue. Réessaie dans un instant.
        </p>
      )}
    </div>
  );
}
